import type { AxiosError, AxiosResponse } from 'axios'
import { useCallback, useEffect, useState } from 'react'
import type { MouseEvent, VFC } from 'react'
import { HttpClient, serializeJSONApiData } from '@opallabs/api'
import { Spacing } from '@opallabs/gem'
import { type ErrorDocument } from '@opallabs/json-api'
import { usePrevious, useServerErrors } from '@opallabs/lambda'
import { Button, PasswordInputs, TypeAttribute } from '@opallabs/lapidary'
import { LayoutWithSidebar } from '../layoutWithSidebar'
import * as styles from './styles'
import type {
  FormSuccessResponse,
  RequestData,
  ResetPassphraseProps,
} from './types'

export const ResetPassphrase: VFC<ResetPassphraseProps> = ({
  passwordRules,
  theme = 'light',
}) => {
  const [password, setPassword] = useState<string>('')
  const lastPassword = usePrevious(password)
  const [validPassword, setValidPassword] = useState<boolean>(false)

  const { handleServerError, serverErrors, unsetServerErrors } =
    useServerErrors()

  useEffect(() => {
    // Without this short-circuit, *every* change to `password` results in
    // `unsetServerErrors` being called. Because of how state is passed to
    // `<PasswordInputs>`, this means that submitting the form and getting back
    // a server error relating to a password field will make `<PasswordInputs>`
    // re-render, which triggers this `useEffect`, and so the server errors
    // automatically be unset immediately after they're set.
    //
    // This short-circuit ensures the expected behavior: if you edit a password
    // input the error will be automatically removed, and if you submit an
    // invalid password the relevant error will be displayed.
    if (lastPassword === password) return

    unsetServerErrors(['confirm_password', 'new_password'])
  }, [lastPassword, password, unsetServerErrors])

  useEffect(() => {
    if (Object.keys(serverErrors).length > 0) setValidPassword(false)
  }, [serverErrors, setValidPassword])

  const submitData = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()

      const data = serializeJSONApiData<RequestData>('password', {
        new_password: password,
        confirm_password: password,
      })

      try {
        type Response = AxiosResponse<FormSuccessResponse>
        const response = await HttpClient().patch<typeof data, Response>(
          '/legacy/people/change_password',
          data,
          {
            headers: {
              'Content-Type': 'application/vnd.api+json',
            },
          }
        )
        window.location.assign(response.data.destination)
      } catch (error) {
        handleServerError(error as AxiosError<ErrorDocument>)
      }
    },
    [handleServerError, password]
  )

  return (
    <LayoutWithSidebar theme={theme}>
      <main css={styles.container}>
        <form css={styles.content}>
          <h2 css={styles.formTitle}>Reset your password</h2>

          <PasswordInputs
            explicitErrors={serverErrors}
            onChange={setPassword}
            onValidate={setValidPassword}
            rules={passwordRules}
            styleOverrides={{
              container: styles.fieldset,
              passwordConfirmation: {
                marginInlineStart: Spacing.M * 1.125,
              },
            }}
            theme={theme}
          />

          <footer css={styles.footer}>
            <Button
              ariaLive="polite"
              buttonText="Update Password"
              variant="primary"
              disabled={!validPassword}
              name="submit"
              onClick={submitData}
              theme={theme}
              typeAttribute={TypeAttribute.Submit}
            />
          </footer>
        </form>
      </main>
    </LayoutWithSidebar>
  )
}
ResetPassphrase.displayName = 'Ex Astra: ResetPassphrase'
